import { useReducer, type Dispatch } from 'react';

export const NAVIGATE = 'NAVIGATE' as const;
export const CHANGE_PATH = 'CHANGE_PATH' as const;
const ON_POP_STATE = 'ON_POP_STATE' as const;
const SUBSCRIBED = 'SUBSCRIBED' as const;
import {
    localeSegmentList,
    handleLocaleUrl,
    GLOBAL_CLIENT_ONLY_LOCALE,
} from 'dibs-intl/exports/urls';

type ActionType = typeof NAVIGATE | typeof CHANGE_PATH | typeof ON_POP_STATE | typeof SUBSCRIBED;

const localesSegmentRegExp = `(/(${localeSegmentList.filter(Boolean).join('|')}))?`;

export function isPhotosPath(path: string): boolean {
    return !!new RegExp(`^${localesSegmentRegExp}/photos(/)?`).test(path);
}

export function isPhotoPath(path: string): boolean {
    return !!new RegExp(`^${localesSegmentRegExp}/photo[^s]?($|/)`).test(path);
}

type HistoryReducerAction = {
    type: ActionType;
    path?: string | undefined;
};

type HistoryReducerState = {
    history: History | null;
    onPopStateSubscribed: boolean;
    path: string;
    poppedPath: string;
};

function historyReducer(
    state: HistoryReducerState,
    action: HistoryReducerAction
): HistoryReducerState {
    const { type, path = '' } = action;
    const localizedPath = handleLocaleUrl(path, GLOBAL_CLIENT_ONLY_LOCALE);
    switch (type) {
        case NAVIGATE:
            if (state.history) {
                state.history.pushState(localizedPath, '', localizedPath);
            }
            return {
                ...state,
                path: localizedPath,
            };
        case CHANGE_PATH:
            return { ...state, path: localizedPath };
        case ON_POP_STATE:
            return { ...state, poppedPath: localizedPath };
        case SUBSCRIBED:
            return { ...state, onPopStateSubscribed: true };
        default:
            return state;
    }
}

export type UseHistoryReducer = {
    history: HistoryReducerState;
    dispatchHistory: Dispatch<HistoryReducerAction>;
};

export function useHistoryReducer({ path }: { path: string }): UseHistoryReducer {
    const [history, dispatchHistory] = useReducer(historyReducer, {
        history: typeof window === 'undefined' ? null : window.history,
        onPopStateSubscribed: false,
        path,
        poppedPath: path,
    });

    if (history.history && !history.onPopStateSubscribed) {
        dispatchHistory({ type: SUBSCRIBED });
        window.onpopstate = () => {
            const { pathname, search } = window.location;
            dispatchHistory({
                type: ON_POP_STATE,
                path: handleLocaleUrl(pathname + search, GLOBAL_CLIENT_ONLY_LOCALE),
            });
        };
    }

    return {
        history,
        dispatchHistory,
    };
}
